/**
 * explorationActions - Write atoms for discovering relationships and toggling
 * relationship selections (loading connected nodes).
 *
 * These actions handle the async network operations for discovering which
 * relationship types exist for a node, and loading the actual connected nodes
 * when a user toggles a relationship pill.
 */
import { atom, type WritableAtom } from 'jotai';

import { cloudIdAtom } from '../../../../../../../ui/state';
import { ExecuteQueryAtom } from '../../../../../state/ExecuteActionAtom';
import { getGraphQLEndpointUrl, JSON_CONTENT_TYPE } from '../../../../../state/graphqlEndpoints';
import { generateCypherQueryFromConcreteTypes } from '../../../../../state/GraphQLFragmentGenerator';
import { convertLabelsToGraphQLTypes } from '../../../../../state/GraphQLQueryFromLabels';
import { GraphQLSchemaTypesByNameAtom } from '../../../../../state/GraphQLSchemaAtoms';
import {
	ExplorationCypherOverrideAtom,
	ExplorationGraphQLOverrideAtom,
	GraphQLLimitAtom,
	HeadersAtom,
	NetworkResultAtom,
} from '../../../../../state/LabsAtoms';
import { DEFAULT_TENANT_BASE_URL } from '../../../../labs/home/constants';
import type { Relationship } from '../../../../labs/home/hooks/useRelationships';
import { executeAggRequest, extractOperationName } from '../../../../labs/home/utils/requestUtils';
import { buildSiteAri } from '../../../../labs/home/utils/siteUtils';
import {
	QueryCacheAtom,
	ExploredNodeArisAtom,
	InflightDiscoveryAtom,
	type InflightDiscoveryResult,
} from '../atoms/cacheAtoms';
import { DisplayOutgoingAtom, DisplayIncomingAtom } from '../atoms/derivedAtoms';
import {
	DiscoveredOutgoingAtom,
	DiscoveredIncomingAtom,
	DiscoveryStatusAtom,
	ExplorationErrorAtom,
	LoadingRelKeysAtom,
	DiscoveryFailuresAtom,
} from '../atoms/explorationAtoms';
import {
	FocusDirectionAtom,
	EffectiveExplorationNodeAtom,
	SelectedRelationshipsAtom,
	type SelectedRelationship,
} from '../atoms/focusAtoms';
import type { ObjectNodeData } from '../nodes/ObjectNode';
import {
	ATLASSIAN_USER_BRIDGE,
	EXTERNAL_ENTITY_BRIDGE,
	isAtiThirdParty,
} from '../services/bridgeRelationships';
import {
	buildBatchDiscoveryQueryFromShapes,
	buildLoadNodesQuery,
	buildResolveExternalPeerQuery,
	classifyRelationshipShape,
	enumerateAtlassianUserTransitiveEntries,
	enumerateExternalPeerTransitiveEntries,
	escapeLabel,
	parseDiscoveryResult,
	getDiscoveryNodeDedupKey,
	getTargetNodeLabels,
	groupRelationshipsByProduct,
	makeNodesCacheKey,
	makeDiscoveryCacheKey,
	makeSelectedRelKey,
	isCacheValid,
	isGraphQLQueryFailure,
	extractTraceId,
	isContentEntityLinksType,
	mergeRelationshipResultsByType,
	splitContentEntityByNodeType,
	buildSyntheticResult,
	EXPLORATION_NODE_LIMIT,
	CONTENT_ENTITY_LINKS_ENTITY,
	COUNT_ONLY_GRAPHQL_QUERY,
	CURRENT_USER_ARI_PROXY,
	ROOT_DISCOVERY_RELATIONSHIP,
	type ExplorationDirection,
	type RelationshipInfo,
	type RelationshipShape,
	type DiscoveredNode,
	type DirectedRelationshipInfo,
	type CachedQueryResult,
	type ProductBatchEntry,
	type BatchDiscoveryParsedResult,
} from '../services/cypherQueryService';
import {
	twgRelationshipsAsyncAtom,
	twgNodesAsyncAtom,
	AtiNodeMetadataMapAtom,
} from '../services/twgSchemaService';
import {
	resolveNodeTypenamesFromAris,
	atiStringFromAri,
	type AtiNodeMetadata,
} from '../utils/ariUtils';

// ============================================================================
// Internal: Standalone discovery query execution
// ============================================================================

const MAX_CONCURRENT_GRAPHQL_REQUESTS = 5;

let activeGraphQLRequests = 0;
const graphQLRequestQueue: Array<() => void> = [];

function runNextGraphQLRequest() {
	if (activeGraphQLRequests >= MAX_CONCURRENT_GRAPHQL_REQUESTS) {
		return;
	}
	const next = graphQLRequestQueue.shift();
	if (next) {
		next();
	}
}

function enqueueGraphQLRequest<T>(run: () => Promise<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		const start = () => {
			activeGraphQLRequests++;
			run()
				.then(resolve, reject)
				.finally(() => {
					activeGraphQLRequests--;
					runNextGraphQLRequest();
				});
		};

		if (activeGraphQLRequests < MAX_CONCURRENT_GRAPHQL_REQUESTS) {
			start();
		} else {
			graphQLRequestQueue.push(start);
		}
	});
}

function ariMatchProps(nodeAri: string): string {
	return nodeAri === CURRENT_USER_ARI_PROXY ? '{ari: $me}' : `{ari: "${nodeAri}"}`;
}

async function waitForGraphQLTypesByName(get: (atom: any) => any) {
	let typesByName = get(GraphQLSchemaTypesByNameAtom);
	if (typesByName) {
		return typesByName;
	}
	for (let attempt = 0; attempt < 15; attempt++) {
		await new Promise((resolve) => setTimeout(resolve, 500));
		typesByName = get(GraphQLSchemaTypesByNameAtom);
		if (typesByName) {
			return typesByName;
		}
	}
	return null;
}

/**
 * Executes a discovery query directly via HTTP, bypassing the shared
 * ExecuteQueryAtom pipeline. This allows multiple discovery queries to
 * run concurrently without racing on shared mutable atoms
 * (ExplorationCypherOverrideAtom, ExplorationGraphQLOverrideAtom, NetworkResultAtom).
 */
async function executeDiscoveryFetch(
	cypherQuery: string,
	graphQLQuery: string,
	userHeaders: Record<string, string>,
	cloudId: string | null,
): Promise<any> {
	const aggBaseUrl = DEFAULT_TENANT_BASE_URL;
	const url = getGraphQLEndpointUrl('/gateway/api/graphql', aggBaseUrl);

	const defaultHeaders: Record<string, string> = {
		accept: 'application/json, multipart/mixed, multipart/mixed; deferSpec=20220824',
		'content-type': JSON_CONTENT_TYPE,
	};

	// Add X-Query-Context header from the unified microsite cloudIdAtom.
	if (cloudId) {
		defaultHeaders['X-Query-Context'] = buildSiteAri(cloudId);
	}

	const variables = {
		cypherQuery,
		params: {},
		version: 'V3',
	};

	const operationName = extractOperationName(graphQLQuery);

	const requestBody = {
		query: graphQLQuery,
		variables,
		...(operationName && { operationName }),
	};

	const finalHeaders = { ...defaultHeaders, ...userHeaders };

	return enqueueGraphQLRequest(() =>
		executeAggRequest({
			url,
			headers: finalHeaders,
			body: requestBody,
			timeoutMs: 30000,
		}),
	);
}

// ============================================================================
// Internal: Execute a batch discovery query
// ============================================================================

/** Return type for ExecuteBatchDiscoveryAtom */
interface BatchDiscoveryOutput {
	parsed: BatchDiscoveryParsedResult;
	rawResponse: any;
	rootNode?: DiscoveredNode;
}

/**
 * Low-level write atom that executes a batch discovery query and returns
 * the parsed result. Used internally by DiscoverForNodeAtom.
 *
 * Uses a standalone HTTP fetch so that multiple batches (e.g. outgoing
 * and incoming) can run concurrently without conflicting on shared atoms.
 *
 * Each entry can carry a precomputed `shape` (used as-is) or a registry
 * `relationship` (classified per the focused node typename). When neither
 * is supplied, a plain direct shape is used.
 */
const ExecuteBatchDiscoveryAtom = atom(
	null,
	async (
		get,
		_set,
		payload: {
			nodeAri: string;
			focusedTypename?: string;
			entries: ProductBatchEntry[];
			direction: ExplorationDirection;
			includeRootNode?: boolean;
		},
	): Promise<BatchDiscoveryOutput> => {
		const atiNodeMap = get(AtiNodeMetadataMapAtom);
		const allRelationships = await get(twgRelationshipsAsyncAtom);

		// Resolve a `RelationshipShape` per entry. The shape drives both the
		// Cypher topology and the set of target typenames we hydrate for.
		const shapes: RelationshipShape[] = [];
		const relLabelsForParsing: string[] = [];

		for (const entry of payload.entries) {
			let shape: RelationshipShape | null = entry.shape ?? null;
			if (!shape && entry.relationship && allRelationships) {
				shape = classifyRelationshipShape(
					entry.relationship,
					entry.relLabel,
					entry.direction,
					atiNodeMap,
					payload.focusedTypename,
				);
			}
			if (!shape) {
				shape = { kind: 'direct', relLabel: entry.relLabel, direction: entry.direction };
			}
			shapes.push(shape);
			relLabelsForParsing.push(entry.relLabel);
		}

		const query = buildBatchDiscoveryQueryFromShapes(payload.nodeAri, shapes, {
			includeRootNode: payload.includeRootNode,
		});

		// Generate a GraphQL query that covers all target node types for the
		// batched relationship labels. Without this the AGG endpoint would not
		// know which fragments to use for hydrating the discovered nodes,
		// leaving them without proper __typename or properties.
		let graphQLQuery = '';
		const nodesMapRes = await get(twgNodesAsyncAtom);
		const nodesMap = nodesMapRes.success ? nodesMapRes.data : {};

		if (allRelationships && nodesMap) {
			const allTargetLabels: string[] = collectHydrationTargetLabels(
				shapes,
				allRelationships,
				atiNodeMap,
			);
			if (payload.includeRootNode) {
				allTargetLabels.push('AtlassianUser');
			}
			const uniqueTargetLabels = [...new Set(allTargetLabels)];

			if (uniqueTargetLabels.length > 0) {
				const graphQLTypes = convertLabelsToGraphQLTypes(uniqueTargetLabels, nodesMap);
				if (graphQLTypes.length > 0) {
					const typesByName = await waitForGraphQLTypesByName(get);
					if (typesByName) {
						const graphQLLimit = get(GraphQLLimitAtom);
						const queryResult = generateCypherQueryFromConcreteTypes(
							graphQLTypes,
							typesByName,
							{
								depth: 2,
								includeTypename: true,
								skipDeprecated: true,
								skipExperimental: true,
								autoSelectIdForObjects: true,
							},
							graphQLLimit,
						);
						if (queryResult) {
							graphQLQuery = queryResult.query;
						}
					}
				}
			}
		}

		if (!graphQLQuery) {
			return {
				parsed: parseDiscoveryResult(undefined, relLabelsForParsing, atiNodeMap),
				rawResponse: undefined,
			};
		}

		// Read connection config atoms once — these are stable across the
		// concurrent batches and are only read, never written.
		const userHeaders = get(HeadersAtom);
		const cloudId = get(cloudIdAtom) as string | null;

		const result = await executeDiscoveryFetch(query, graphQLQuery, userHeaders, cloudId);

		const parsed = parseDiscoveryResult(
			result,
			payload.includeRootNode
				? [ROOT_DISCOVERY_RELATIONSHIP, ...relLabelsForParsing]
				: relLabelsForParsing,
			atiNodeMap,
			payload.includeRootNode
				? [{ relLabel: ROOT_DISCOVERY_RELATIONSHIP, direction: 'outgoing' }, ...payload.entries]
				: payload.entries,
			// Pass the focused node's ARI down so transitively-discovered
			// graph-bridged nodes (e.g. an `ari:cloud:graph::deployment/...`
			// reached via a Bitbucket repository expansion) can inherit the
			// connector identity for `productDisplayName` even when AGG
			// didn't return a `thirdPartyId` on the bridged node itself.
			payload.nodeAri,
		);
		const rootRel = parsed.resolved.find((rel) => rel.type === ROOT_DISCOVERY_RELATIONSHIP);

		return {
			parsed: {
				resolved: parsed.resolved.filter((rel) => rel.type !== ROOT_DISCOVERY_RELATIONSHIP),
				truncatedLabels: parsed.truncatedLabels.filter(
					(label) => label !== ROOT_DISCOVERY_RELATIONSHIP,
				),
			},
			rawResponse: result,
			rootNode: rootRel?.nodes?.[0],
		};
	},
);

/**
 * Collects the set of TWG node typenames the discovery hydration query
 * must cover for a batch of `RelationshipShape` entries.
 *
 * - For `direct` and `mixed` cloud branches, we use the registry's target
 *   typenames as before.
 * - For `multihop-third-party` and the third-party slice of `mixed`, we
 *   also need the External graph peer typenames so AGG can hydrate the
 *   final node returned by the multi-hop.
 * - For `inverse-third-party-user`, we add `AtlassianUser`.
 * - For `transitive-from-atlassian-user`, we add the rel's regular target
 *   typenames (the relationship is rooted at ThirdPartyUser but ends at
 *   the same target as the registry entry).
 */
function collectHydrationTargetLabels(
	shapes: RelationshipShape[],
	allRelationships: Record<string, any>,
	atiNodeMap: Record<string, AtiNodeMetadata>,
): string[] {
	const labels: string[] = [];

	for (const shape of shapes) {
		const registryTargets = getTargetNodeLabels(allRelationships, shape.relLabel, shape.direction);

		switch (shape.kind) {
			case 'direct':
				labels.push(...registryTargets);
				break;
			case 'multihop-third-party': {
				// Also expose all known External-graph typenames as candidates.
				const externalTypenames = collectExternalGraphTypenames(atiNodeMap);
				labels.push(...registryTargets, ...externalTypenames);
				break;
			}
			case 'mixed':
				labels.push(
					...registryTargets,
					...shape.cloudTypenames,
					...collectExternalGraphTypenames(atiNodeMap),
				);
				break;
			case 'inverse-third-party-user':
				labels.push('AtlassianUser');
				break;
			case 'transitive-from-atlassian-user':
				labels.push(...registryTargets);
				break;
			case 'transitive-from-external-peer':
				// The opposite end determines what hydration shape AGG needs:
				//   'atlassian-user' → an AtlassianUser fragment
				//   'external'       → all External graph fragments (the
				//                      bridge can land on any of them)
				//   'direct'         → use registry targets as-is
				if (shape.otherEnd === 'atlassian-user') {
					labels.push('AtlassianUser');
				} else if (shape.otherEnd === 'external') {
					labels.push(...collectExternalGraphTypenames(atiNodeMap));
				} else {
					labels.push(...registryTargets);
				}
				break;
		}
	}

	return labels;
}

/**
 * Returns every typename whose ATI lives under `ari:cloud:graph::*`.
 * Used as the hydration set for multi-hop bridge results so AGG knows
 * which fragments to inline.
 */
function collectExternalGraphTypenames(atiNodeMap: Record<string, AtiNodeMetadata>): string[] {
	const typenames = new Set<string>();
	for (const [ati, meta] of Object.entries(atiNodeMap)) {
		if (ati.startsWith('ati:cloud:graph:')) {
			typenames.add(meta.typename);
		}
	}
	return Array.from(typenames);
}

/**
 * Resolves the External graph peer ARI for a third-party bridge node.
 *
 * The graph store stores `ari:third-party:*` nodes only as bridge points
 * for permission checks; their real graph counterparts live under
 * `ari:cloud:graph::*` and carry the `thirdPartyId` property the rest of
 * the explorer needs. This helper issues one tiny Cypher hop to find the
 * peer so the focused-node ARI can be rewritten before discovery starts.
 *
 * Returns null if the peer can't be found (in which case the caller
 * should continue with the original ARI).
 */
async function resolveExternalPeerAri(
	get: (atom: any) => any,
	thirdPartyAri: string,
): Promise<string | null> {
	const userHeaders = get(HeadersAtom);
	const cloudId = get(cloudIdAtom) as string | null;

	const query = buildResolveExternalPeerQuery(thirdPartyAri);
	try {
		const result = await executeDiscoveryFetch(
			query,
			COUNT_ONLY_GRAPHQL_QUERY,
			userHeaders,
			cloudId,
		);
		const edges = result?.data?.graphStore?.cypherQueryV2?.edges;
		if (!Array.isArray(edges)) {
			return null;
		}
		for (const edge of edges) {
			const columns = edge?.node?.columns;
			if (!Array.isArray(columns)) {
				continue;
			}
			for (const col of columns) {
				if (col.key === 'c' && col.value?.id) {
					return col.value.id as string;
				}
			}
		}
	} catch (error) {}
	return null;
}

// ============================================================================
// Discover relationships for a node (product-based parallel batching)
// ============================================================================

/**
 * Internal helper: discover relationships for a single product batch.
 *
 * Each product batch can contain both outgoing and incoming labels. Multiple
 * batches can run concurrently because they use the standalone
 * `executeDiscoveryFetch` instead of shared atoms.
 *
 * Handles scan-limit truncation by iteratively re-batching truncated labels.
 */
/**
 * Result from a product batch discovery, including resolved relationships
 * and any per-direction failure trace IDs.
 */
interface ProductBatchResult {
	resolved: DirectedRelationshipInfo[];
	/** Trace IDs from failed responses, keyed by direction. */
	failedTraceIds: Map<ExplorationDirection, string[]>;
	rootNode?: DiscoveredNode;
}

const DiscoverProductBatchAtom = atom(
	null,
	async (
		get,
		set,
		payload: {
			nodeData: ObjectNodeData;
			focusedTypename?: string;
			entries: ProductBatchEntry[];
			includeRootNode?: boolean;
		},
	): Promise<ProductBatchResult> => {
		const { nodeData, entries, focusedTypename, includeRootNode } = payload;
		const resolved: DirectedRelationshipInfo[] = [];
		const failedTraceIds = new Map<ExplorationDirection, string[]>();
		let pendingEntries = entries;
		let rootNode: DiscoveredNode | undefined;
		const maxIterations = entries.length + 5;
		let iteration = 0;

		while (pendingEntries.length > 0 && iteration < maxIterations) {
			iteration++;
			const directionByLabel = new Map<string, ExplorationDirection>();
			for (const entry of pendingEntries) {
				directionByLabel.set(entry.relLabel, entry.direction);
			}
			const directionsInBatch = [...new Set(pendingEntries.map((entry) => entry.direction))];
			const batchOutput = await set(ExecuteBatchDiscoveryAtom, {
				nodeAri: nodeData.id,
				focusedTypename,
				entries: pendingEntries,
				direction: directionsInBatch[0] ?? 'outgoing',
				includeRootNode: includeRootNode && iteration === 1,
			});

			if (!batchOutput) {
				break;
			}

			const { parsed: batchResult, rawResponse } = batchOutput;
			rootNode = rootNode ?? batchOutput.rootNode;

			if (isGraphQLQueryFailure(rawResponse)) {
				const traceId = extractTraceId(rawResponse);
				if (traceId) {
					for (const direction of directionsInBatch) {
						const existing = failedTraceIds.get(direction) ?? [];
						failedTraceIds.set(direction, [...existing, traceId]);
					}
				}
			}

			const entryByLabel = new Map<string, ProductBatchEntry>();
			for (const e of pendingEntries) {
				entryByLabel.set(e.relLabel, e);
			}
			const toEntries = (labels: string[]): ProductBatchEntry[] =>
				labels
					.map((label) => entryByLabel.get(label))
					.filter((entry): entry is ProductBatchEntry => !!entry);
			const withDirection = (
				rel: RelationshipInfo & { _direction?: ExplorationDirection },
			): DirectedRelationshipInfo => ({
				...rel,
				_direction: rel._direction ?? directionByLabel.get(rel.type) ?? 'outgoing',
			});

			if (batchResult.truncatedLabels.length === 0) {
				resolved.push(...batchResult.resolved.map(withDirection));
				pendingEntries = [];
			} else if (batchResult.resolved.length > 1) {
				resolved.push(...batchResult.resolved.slice(0, -1).map(withDirection));
				const lastResolved = batchResult.resolved[batchResult.resolved.length - 1];
				pendingEntries = toEntries([lastResolved.type, ...batchResult.truncatedLabels]);
			} else if (batchResult.resolved.length === 1) {
				resolved.push(...batchResult.resolved.map(withDirection));
				pendingEntries = toEntries(batchResult.truncatedLabels);
			} else {
				pendingEntries = [];
			}
		}

		return { resolved, failedTraceIds, rootNode };
	},
);

// ============================================================================
// Special case: content-entity-links-entity two-phase discovery
// ============================================================================

/**
 * Builds a single bidirectional Cypher query for content-entity-links-entity
 * that discovers both outgoing and incoming connections in one request.
 *
 * The query uses UNION ALL with two blocks — one for each direction — and
 * encodes the direction as a string column so the parser can split results.
 */
/**
 * Builds the bidirectional content-entity-links-entity probe/hydrate query.
 *
 * Each direction emits TWO UNION ALL blocks:
 *   1. Direct block  — catches normal cloud entities.
 *   2. Bridge block  — catches entities that sit behind the
 *      `external_entity_is_third_party_entity` bridge so that the
 *      External graph peer (e.g. ExternalRepository) is returned
 *      rather than the opaque ThirdParty node.
 *
 * Both blocks share the same `direction` column literal so that
 * `parseBidirectionalProbeResult` can split results by direction
 * without knowing which block produced each row.  The `via` column
 * (injected by the bridge block) lets downstream dedup prefer the
 * direct row whenever both blocks surface the same ARI.
 */
function buildBidirectionalContentEntityQuery(nodeAri: string): string {
	const label = escapeLabel(CONTENT_ENTITY_LINKS_ENTITY);
	const bridgeLabel = escapeLabel(EXTERNAL_ENTITY_BRIDGE.relName);

	return [
		// Outgoing — direct
		`MATCH (n ${ariMatchProps(nodeAri)})`,
		`OPTIONAL MATCH (n)-[r1:${label}]->(m1)`,
		`RETURN m1 AS c, "outgoing" AS direction, "direct" AS via, r1.lastUpdated AS relLastUpdated`,
		`ORDER BY r1.lastUpdated DESC`,
		`LIMIT ${EXPLORATION_NODE_LIMIT}`,
		``,
		`UNION ALL`,
		``,
		// Outgoing — bridge (through external_entity_is_third_party_entity)
		`MATCH (n ${ariMatchProps(nodeAri)})`,
		`OPTIONAL MATCH (n)-[r1b:${label}]->(tp1b)<-[b1b:${bridgeLabel}]-(m1b)`,
		`RETURN m1b AS c, "outgoing" AS direction, "bridge" AS via, b1b.lastUpdated AS relLastUpdated`,
		`ORDER BY r1b.lastUpdated DESC`,
		`LIMIT ${EXPLORATION_NODE_LIMIT}`,
		``,
		`UNION ALL`,
		``,
		// Incoming — direct
		`MATCH (n ${ariMatchProps(nodeAri)})`,
		`OPTIONAL MATCH (n)<-[r2:${label}]-(m2)`,
		`RETURN m2 AS c, "incoming" AS direction, "direct" AS via, r2.lastUpdated AS relLastUpdated`,
		`ORDER BY r2.lastUpdated DESC`,
		`LIMIT ${EXPLORATION_NODE_LIMIT}`,
		``,
		`UNION ALL`,
		``,
		// Incoming — bridge
		`MATCH (n ${ariMatchProps(nodeAri)})`,
		`OPTIONAL MATCH (n)<-[r2b:${label}]-(tp2b)<-[b2b:${bridgeLabel}]-(m2b)`,
		`RETURN m2b AS c, "incoming" AS direction, "bridge" AS via, b2b.lastUpdated AS relLastUpdated`,
		`ORDER BY r2b.lastUpdated DESC`,
		`LIMIT ${EXPLORATION_NODE_LIMIT}`,
	].join('\n');
}

/**
 * Parses a bidirectional probe result, splitting ARIs by direction.
 */
function parseBidirectionalProbeResult(result: any): {
	outgoingAris: string[];
	incomingAris: string[];
} {
	const edges = result?.data?.graphStore?.cypherQueryV2?.edges;
	const outgoingAris: string[] = [];
	const incomingAris: string[] = [];

	if (!Array.isArray(edges)) {
		return { outgoingAris, incomingAris };
	}

	// Track ARIs seen via the direct block so bridge-derived ARIs for the
	// same entity are suppressed (same direct-over-bridge preference as the
	// rest of the discovery pipeline).
	const outgoingDirectAris = new Set<string>();
	const incomingDirectAris = new Set<string>();

	for (const edge of edges) {
		const columns = edge?.node?.columns;
		if (!Array.isArray(columns)) {
			continue;
		}

		let ari: string | null = null;
		let direction: string | null = null;
		let via = 'direct';

		for (const col of columns) {
			if (col.key === 'c' && col.value?.id) {
				ari = col.value.id;
			} else if (col.key === 'direction') {
				direction = col.value?.str_val ?? col.value?.value ?? null;
			} else if (col.key === 'via') {
				via = col.value?.str_val ?? col.value?.value ?? 'direct';
			}
		}

		if (!ari || !direction) {
			continue;
		}

		// Resolve the ARI to its ATI so we can check whether this is a
		// third-party bridge node using the standard `isAtiThirdParty`
		// predicate. Raw third-party ARIs (e.g.
		// `ari:third-party:google.google-drive::document/...`) have an ATI
		// of `ati:third-party:google.google-drive:document` which
		// `isAtiThirdParty` correctly identifies.
		//
		// We suppress these because the bridge arm of the probe query will
		// return their External peer ARIs instead. Keeping both would result
		// in the ThirdParty node AND the External node appearing as two
		// separate linked relationships for the same logical entity.
		const atiStr = atiStringFromAri(ari);
		if (isAtiThirdParty(atiStr)) {
			continue;
		}

		if (direction === 'outgoing') {
			if (via === 'direct') {
				outgoingDirectAris.add(ari);
				if (!outgoingAris.includes(ari)) {
					outgoingAris.push(ari);
				}
			} else if (!outgoingDirectAris.has(ari) && !outgoingAris.includes(ari)) {
				outgoingAris.push(ari);
			}
		} else if (direction === 'incoming') {
			if (via === 'direct') {
				incomingDirectAris.add(ari);
				if (!incomingAris.includes(ari)) {
					incomingAris.push(ari);
				}
			} else if (!incomingDirectAris.has(ari) && !incomingAris.includes(ari)) {
				incomingAris.push(ari);
			}
		}
	}

	return { outgoingAris, incomingAris };
}

/**
 * Parses a bidirectional hydrated result into per-direction, per-node-type
 * RelationshipInfo arrays.
 *
 * The hydrated response has two kinds of rows (outgoing and incoming),
 * distinguished by the "direction" string column. Nodes are collected
 * per direction, then split by typename using splitContentEntityByNodeType()
 * so each node type appears as a separate entry in the Explore panel.
 */
function parseBidirectionalHydratedResult(
	result: any,
	atiNodeMap: Record<string, AtiNodeMetadata>,
	inheritedProductAri?: string | null,
): { outgoing: RelationshipInfo[]; incoming: RelationshipInfo[] } {
	const edges = result?.data?.graphStore?.cypherQueryV2?.edges;

	// Per-direction maps: logical node identity → {node, via} so we can apply
	// direct-over-bridge preference at the hydration stage while also collapsing
	// activation-specific duplicates of the same external entity.
	const outMap = new Map<string, { node: DiscoveredNode; via: string }>();
	const inMap = new Map<string, { node: DiscoveredNode; via: string }>();

	if (Array.isArray(edges)) {
		for (const edge of edges) {
			const columns = edge?.node?.columns;
			if (!Array.isArray(columns)) {
				continue;
			}

			let direction: string | null = null;
			let nodeValue: any = null;
			let via = 'direct';

			for (const col of columns) {
				if (col.key === 'direction') {
					direction = col.value?.str_val ?? col.value?.value ?? null;
				} else if (col.key === 'c') {
					nodeValue = col.value;
				} else if (col.key === 'via') {
					via = col.value?.str_val ?? col.value?.value ?? 'direct';
				}
			}

			if (!direction || !nodeValue) {
				continue;
			}

			// Use the standard hydration helper from parseDiscoveryResult,
			// forwarding the `via` column so its own per-batch dedup handles
			// third-party node drops defensively.
			const parsed = parseDiscoveryResult(
				{
					data: {
						graphStore: {
							cypherQueryV2: {
								edges: [
									{
										node: {
											columns: [
												{ key: 'c', value: nodeValue },
												{
													key: 'relationship',
													value: { str_val: CONTENT_ENTITY_LINKS_ENTITY },
												},
												{ key: 'via', value: { str_val: via } },
											],
										},
									},
								],
							},
						},
					},
				},
				[CONTENT_ENTITY_LINKS_ENTITY],
				atiNodeMap,
				undefined,
				inheritedProductAri,
			);

			const nodes = parsed.resolved[0]?.nodes ?? [];
			const targetMap = direction === 'outgoing' ? outMap : inMap;

			for (const n of nodes) {
				const dedupKey = getDiscoveryNodeDedupKey(n);
				const existing = targetMap.get(dedupKey);
				if (!existing) {
					targetMap.set(dedupKey, { node: n, via });
				} else if (existing.via !== 'direct' && via === 'direct') {
					// Prefer the direct row over the bridge row when both appear.
					targetMap.set(dedupKey, { node: n, via });
				}
			}
		}
	}

	const outNodes = Array.from(outMap.values()).map((e) => e.node);
	const inNodes = Array.from(inMap.values()).map((e) => e.node);

	return {
		outgoing: splitContentEntityByNodeType(outNodes),
		incoming: splitContentEntityByNodeType(inNodes),
	};
}

/**
 * Discovers content-entity-links-entity relationships using a two-phase approach
 * with a single bidirectional Cypher query (both outgoing and incoming combined):
 *
 * Phase 1 (Probe): Run a bidirectional Cypher query using COUNT_ONLY_GRAPHQL_QUERY
 *   - Returns raw ARIs without hydration, tagged with direction
 *   - Extracts the ARIs of connected nodes per direction
 *   - Uses ARI parsing to determine which node types are present
 *
 * Phase 2 (Hydrate): If nodes were found, re-run the same bidirectional query
 *   but with precisely the needed hydration fragments generated from
 *   the discovered node types via convertLabelsToGraphQLTypes()
 *
 * @returns Per-direction, per-node-type results: { outgoing: RelationshipInfo[], incoming: RelationshipInfo[] }
 */
const DiscoverContentEntityLinksAtom = atom(
	null,
	async (
		get,
		_set,
		payload: { nodeAri: string },
	): Promise<{ outgoing: RelationshipInfo[]; incoming: RelationshipInfo[] }> => {
		const emptyResult = {
			outgoing: [] as RelationshipInfo[],
			incoming: [] as RelationshipInfo[],
		};

		// --- Phase 1: Probe (single bidirectional query) ---
		const probeQuery = buildBidirectionalContentEntityQuery(payload.nodeAri);

		const userHeaders = get(HeadersAtom);
		const cloudId = get(cloudIdAtom) as string | null;

		const probeResult = await executeDiscoveryFetch(
			probeQuery,
			COUNT_ONLY_GRAPHQL_QUERY,
			userHeaders,
			cloudId,
		);

		// Parse the probe result to extract ARIs per direction
		const { outgoingAris, incomingAris } = parseBidirectionalProbeResult(probeResult);
		const allAris = [...outgoingAris, ...incomingAris];

		if (allAris.length === 0) {
			return emptyResult;
		}

		// --- Determine node typenames from ARIs ---
		const atiNodeMap = get(AtiNodeMetadataMapAtom);
		const nodeTypenames = resolveNodeTypenamesFromAris(allAris, atiNodeMap);

		if (nodeTypenames.length === 0) {
			// Couldn't determine types — return empty (can't split by type)
			return emptyResult;
		}

		// The bridge blocks in the query can return External graph nodes
		// (ari:cloud:graph::*). Those ARIs resolve via resolveNodeTypenamesFromAris,
		// but to guarantee we get full hydration coverage for ALL possible
		// External peer typenames (not just those already in the probe sample),
		// we union in every known ati:cloud:graph typename. This matches the
		// approach used by collectHydrationTargetLabels for multihop-third-party
		// shapes and ensures hydrateEntityValue can read thirdPartyId off the
		// returned ExternalXxx nodes so productDisplayName is set correctly.
		const allExternalTypenames = collectExternalGraphTypenames(atiNodeMap);
		const hydrationTypenames = [...new Set([...nodeTypenames, ...allExternalTypenames])];

		// --- Phase 2: Re-run with precise hydration fragments ---
		const nodesMapRes = await get(twgNodesAsyncAtom);
		const nodesMap = nodesMapRes.success ? nodesMapRes.data : {};
		const graphQLTypes = convertLabelsToGraphQLTypes(hydrationTypenames, nodesMap);
		const typesByName = get(GraphQLSchemaTypesByNameAtom);

		let graphQLOverride: string | null = null;
		if (graphQLTypes.length > 0 && typesByName) {
			const queryResult = generateCypherQueryFromConcreteTypes(
				graphQLTypes,
				typesByName,
				{
					depth: 2,
					includeTypename: true,
					skipDeprecated: true,
					skipExperimental: true,
					autoSelectIdForObjects: true,
				},
				EXPLORATION_NODE_LIMIT,
			);
			if (queryResult) {
				graphQLOverride = queryResult.query;
			}
		}

		if (!graphQLOverride) {
			// Couldn't generate hydration query — return empty
			return emptyResult;
		}

		// Re-run the same bidirectional query with proper hydration
		const hydratedResult = await executeDiscoveryFetch(
			probeQuery,
			graphQLOverride,
			userHeaders,
			cloudId,
		);

		return parseBidirectionalHydratedResult(hydratedResult, atiNodeMap, payload.nodeAri);
	},
);

// ============================================================================
// Cache helpers
// ============================================================================

/**
 * Caches discovery results and populates per-relationship node caches.
 * Shared helper used by DiscoverForNodeAtom after all product batches complete.
 */
function cacheDiscoveryResults(
	set: (atom: any, updater: any) => void,
	nodeAri: string,
	direction: ExplorationDirection,
	discovered: RelationshipInfo[],
): void {
	const cacheKey = makeDiscoveryCacheKey(nodeAri, direction);
	set(QueryCacheAtom, (prev: Map<string, CachedQueryResult>) => {
		const next = new Map(prev);
		next.set(cacheKey, { result: discovered, cachedAt: Date.now() });

		// Also cache each relationship's discovered nodes individually
		// so ToggleRelationshipAtom can skip the separate load query.
		for (const rel of discovered) {
			if (rel.nodes && rel.nodes.length > 0) {
				const nodesCacheKey = makeNodesCacheKey(nodeAri, direction, rel.type);
				const syntheticResult = {
					data: {
						graphStore: {
							cypherQueryV2: {
								edges: rel.nodes.map((node) => ({
									node: {
										columns: [
											{
												key: 'm',
												value: {
													id: node.id,
													data: {
														__typename: node.typename,
														...node.properties,
													},
												},
											},
										],
									},
								})),
							},
						},
					},
				};
				next.set(nodesCacheKey, {
					result: syntheticResult,
					cachedAt: Date.now(),
				});
			}
		}

		return next;
	});
}

const MAX_DISCOVERY_PRODUCT_BATCHES = 4;

function rebalanceProductGroups(
	groups: Map<string, ProductBatchEntry[]>,
): Map<string, ProductBatchEntry[]> {
	const entries = Array.from(groups.entries()).filter(
		([, batchEntries]) => batchEntries.length > 0,
	);
	if (entries.length <= MAX_DISCOVERY_PRODUCT_BATCHES) {
		return new Map(entries);
	}

	entries.sort((a, b) => b[1].length - a[1].length);
	const kept = entries.slice(0, MAX_DISCOVERY_PRODUCT_BATCHES);
	const overflow = entries.slice(MAX_DISCOVERY_PRODUCT_BATCHES);

	for (const [, batchEntries] of overflow) {
		let smallestKept = kept[0];
		for (const candidate of kept) {
			if (candidate[1].length < smallestKept[1].length) {
				smallestKept = candidate;
			}
		}
		smallestKept[1].push(...batchEntries);
	}

	return new Map(kept);
}

function getBootstrapRootNode(nodeData: ObjectNodeData): DiscoveredNode | undefined {
	const bootstrapUser = nodeData.properties?.bootstrapUser;
	if (!bootstrapUser || typeof bootstrapUser !== 'object') {
		return undefined;
	}

	const id = typeof bootstrapUser.id === 'string' ? bootstrapUser.id : null;
	if (!id) {
		return undefined;
	}

	return {
		id,
		typename: 'AtlassianUser',
		displayName:
			typeof bootstrapUser.displayName === 'string' && bootstrapUser.displayName.length > 0
				? bootstrapUser.displayName
				: 'You',
		url: null,
		properties: {
			email: typeof bootstrapUser.email === 'string' ? bootstrapUser.email : undefined,
			picture: typeof bootstrapUser.picture === 'string' ? bootstrapUser.picture : undefined,
		},
	};
}

// ============================================================================
// Discover relationships for both directions and update panel state
// ============================================================================

/**
 * Discover relationships for a node and update the Explorer panel atoms.
 * Used for both focus mode entry and child drilling.
 *
 * Groups relationships by product domain (extracted from ATI types) and
 * runs all product batches in parallel. Each batch makes its own HTTP
 * request via `executeDiscoveryFetch`, avoiding the shared-atom race
 * condition that would occur with `ExecuteQueryAtom`.
 *
 * Usage: `const discover = useSetAtom(DiscoverForNodeAtom); discover(nodeData);`
 */
export const DiscoverForNodeAtom: WritableAtom<
	null,
	[nodeData: ObjectNodeData],
	Promise<InflightDiscoveryResult | undefined>
> & {
	init: null;
} = atom(null, async (get, set, nodeData: ObjectNodeData) => {
	set(FocusDirectionAtom, 'outgoing');
	set(DiscoveredOutgoingAtom, []);
	set(DiscoveredIncomingAtom, []);
	set(ExplorationErrorAtom, null);
	set(DiscoveryStatusAtom, 'discovering');
	set(DiscoveryFailuresAtom, { outgoing: null, incoming: null });

	// Mark node as explored (for purple dot)
	set(ExploredNodeArisAtom, (prev) => {
		const next = new Set(prev);
		next.add(nodeData.id);
		return next;
	});

	try {
		// Check caches for both directions
		const cache = get(QueryCacheAtom);
		const outCacheKey = makeDiscoveryCacheKey(nodeData.id, 'outgoing');
		const inCacheKey = makeDiscoveryCacheKey(nodeData.id, 'incoming');
		const cachedOut = cache.get(outCacheKey);
		const cachedIn = cache.get(inCacheKey);

		if (isCacheValid(cachedOut) && isCacheValid(cachedIn)) {
			set(DiscoveredOutgoingAtom, cachedOut.result as RelationshipInfo[]);
			set(DiscoveredIncomingAtom, cachedIn.result as RelationshipInfo[]);
			set(DiscoveryStatusAtom, 'discovered');
			return {
				outgoing: cachedOut.result as RelationshipInfo[],
				incoming: cachedIn.result as RelationshipInfo[],
			};
		}

		// Check for an in-flight discovery promise for this node.
		// If another caller already started discovery for the same node,
		// reuse its promise instead of launching duplicate HTTP requests.
		const inflightMap = get(InflightDiscoveryAtom);
		const existingPromise = inflightMap.get(nodeData.id);

		if (existingPromise) {
			const inflightResult = await existingPromise;

			// Staleness check: if the user has navigated to a different node
			// while we were waiting, don't overwrite the panel atoms.
			const currentExplorationNode = get(EffectiveExplorationNodeAtom);
			if (currentExplorationNode && currentExplorationNode.id !== nodeData.id) {
				return;
			}

			set(DiscoveredOutgoingAtom, inflightResult.outgoing);
			set(DiscoveredIncomingAtom, inflightResult.incoming);
			set(DiscoveryStatusAtom, 'discovered');
			return inflightResult;
		}

		// No cache hit and no in-flight promise — create the discovery
		// promise and store it so concurrent callers can reuse it.
		const discoveryPromise = executeDiscovery(get, set, nodeData);

		// Store the in-flight promise keyed by node ARI
		set(InflightDiscoveryAtom, (prev) => {
			const next = new Map(prev);
			next.set(nodeData.id, discoveryPromise);
			return next;
		});

		const result = await discoveryPromise;

		// Staleness check: if the user has navigated to a different node while
		// this discovery was in-flight, don't overwrite the panel atoms — the
		// newer discovery's results should take precedence.
		const currentExplorationNode = get(EffectiveExplorationNodeAtom);
		if (currentExplorationNode && currentExplorationNode.id !== nodeData.id) {
			return;
		}

		set(DiscoveredOutgoingAtom, result.outgoing);
		set(DiscoveredIncomingAtom, result.incoming);
		set(DiscoveryStatusAtom, 'discovered');
		return result;
	} catch (error) {
		// Staleness check: only set error state if this node is still the
		// current exploration target. If the user navigated away while the
		// request was in-flight, silently discard the error.
		const currentExplorationNode = get(EffectiveExplorationNodeAtom);
		if (currentExplorationNode && currentExplorationNode.id !== nodeData.id) {
			return;
		}

		set(
			ExplorationErrorAtom,
			error instanceof Error ? error.message : 'Failed to discover relationships',
		);
		set(DiscoveryStatusAtom, 'error');
		// Set DiscoveryFailuresAtom for network-level errors (e.g. "Failed to
		// fetch") so the error flag UI renders. For network failures we don't
		// have trace IDs, so use empty arrays.
		set(DiscoveryFailuresAtom, {
			outgoing: { traceIds: [] },
			incoming: { traceIds: [] },
		});
	}
});

/**
 * Internal helper that performs the actual discovery work: resolves the node
 * typename, groups relationships by product, launches parallel batches, merges
 * results, and caches them. Returns the discovery result for both directions.
 *
 * Separated from DiscoverForNodeAtom so the returned promise can be stored
 * in InflightDiscoveryAtom for deduplication.
 */
async function executeDiscovery(
	get: (atom: any) => any,
	set: (atom: any, value: any) => any,
	nodeData: ObjectNodeData,
): Promise<InflightDiscoveryResult> {
	try {
		// Resolve the node typename to its canonical name for matching
		const nodesMapRes = await get(twgNodesAsyncAtom);
		const aliasMapToOriginal: Record<string, string> = nodesMapRes.success
			? nodesMapRes.aliasMapToOriginal
			: {};
		const canonicalName = aliasMapToOriginal[nodeData.typename] ?? nodeData.typename;

		// Bridge handling — focused node is a `ari:third-party:*` (a bridge
		// node). Resolve its real `ari:cloud:graph` peer up-front and run
		// discovery from that ARI instead so the bridge is never the root.
		let effectiveNodeAri = nodeData.id;
		if (isAtiThirdParty(nodeData.id)) {
			const resolvedPeerAri = await resolveExternalPeerAri(get, nodeData.id);
			if (resolvedPeerAri) {
				effectiveNodeAri = resolvedPeerAri;
			}
		}

		// Get relationship registry
		const allRelationships = await get(twgRelationshipsAsyncAtom);
		if (!allRelationships || Object.keys(allRelationships).length === 0) {
			throw new Error('Relationship registry not loaded.');
		}

		// Group relationships by product of the "other end" ATI types
		const productGroups = groupRelationshipsByProduct(allRelationships, canonicalName);

		// Bridge handling — focused node is an AtlassianUser. Surface every
		// relationship rooted at the linked ThirdPartyUser as a transitive
		// multi-hop. These synthetic entries get bucketed into a dedicated
		// product group so they execute in their own HTTP batch.
		if (canonicalName === ATLASSIAN_USER_BRIDGE.realTypename) {
			const transitiveEntries = enumerateAtlassianUserTransitiveEntries(allRelationships);
			if (transitiveEntries.length > 0) {
				productGroups.set('__atlassian_user_bridge__', transitiveEntries);
			}
		}

		// Bridge handling — focused node is an `External<X>` graph node.
		// Surface every relationship rooted at (or targeting) the
		// corresponding `ThirdParty<X>` typename via a multi-hop through
		// the External bridge. Without this, External nodes would only see
		// directly cloud-rooted edges and miss everything reachable via
		// their ThirdParty peer (which is most of the interesting links).
		if (canonicalName.startsWith('External')) {
			const externalPeerEntries = enumerateExternalPeerTransitiveEntries(
				allRelationships,
				canonicalName,
			);
			if (externalPeerEntries.length > 0) {
				productGroups.set('__external_peer_bridge__', externalPeerEntries);
			}
		}

		const balancedProductGroups = rebalanceProductGroups(productGroups);

		if (balancedProductGroups.size === 0) {
			const emptyResult: InflightDiscoveryResult = { outgoing: [], incoming: [] };
			cacheDiscoveryResults(set, nodeData.id, 'outgoing', []);
			cacheDiscoveryResults(set, nodeData.id, 'incoming', []);
			return emptyResult;
		}

		const effectiveNodeData: ObjectNodeData =
			effectiveNodeAri === nodeData.id ? nodeData : { ...nodeData, id: effectiveNodeAri };

		// Launch all product batches AND content-entity-links-entity in parallel.
		// content-entity-links-entity is not in the registry so it runs as a
		// separate two-phase (probe→hydrate) discovery alongside the standard
		// product batches.
		const isCurrentUserBootstrap = effectiveNodeAri === CURRENT_USER_ARI_PROXY;
		const batchPromises = Array.from(balancedProductGroups.entries()).map(
			([_productKey, entries], index) =>
				set(DiscoverProductBatchAtom, {
					nodeData: effectiveNodeData,
					focusedTypename: canonicalName,
					entries,
					includeRootNode: isCurrentUserBootstrap && index === 0,
				}),
		);

		const contentEntityPromise = set(DiscoverContentEntityLinksAtom, {
			nodeAri: effectiveNodeAri,
		}).catch((error: unknown) => {
			// content-entity-links-entity discovery is best-effort; log and continue
			return null;
		});

		const [batchResults, contentEntityResult] = await Promise.all([
			Promise.all(batchPromises),
			contentEntityPromise,
		]);

		// Merge results across all batches, splitting by direction.
		const outgoing: RelationshipInfo[] = [];
		const incoming: RelationshipInfo[] = [];
		const outgoingTraceIds: string[] = [];
		const incomingTraceIds: string[] = [];
		let rootNode: DiscoveredNode | undefined;

		for (const batchResult of batchResults) {
			if (!batchResult) {
				continue;
			}
			rootNode = rootNode ?? batchResult.rootNode;
			for (const rel of batchResult.resolved) {
				const { _direction, ...cleanRel } = rel;
				if (_direction === 'outgoing') {
					outgoing.push(cleanRel);
				} else {
					incoming.push(cleanRel);
				}
			}
			// Collect per-direction failure trace IDs
			for (const [direction, traceIds] of batchResult.failedTraceIds) {
				if (direction === 'outgoing') {
					outgoingTraceIds.push(...traceIds);
				} else {
					incomingTraceIds.push(...traceIds);
				}
			}
		}
		if (!rootNode && isCurrentUserBootstrap) {
			rootNode = getBootstrapRootNode(nodeData);
		}

		// Merge content-entity-links-entity results (split by direction and node type)
		if (contentEntityResult) {
			outgoing.push(...contentEntityResult.outgoing);
			incoming.push(...contentEntityResult.incoming);
		}

		// Cross-batch dedup: when the same relationship label was queried in
		// multiple batches (e.g. once via the standard direct path and once
		// via the AtlassianUser-bridge transitive path because the registry
		// listed both fromTypeNames), collapse the per-batch
		// `RelationshipInfo` entries into a single one and prefer direct
		// nodes over bridge nodes for any overlapping ARIs.
		const mergedOutgoing = mergeRelationshipResultsByType(outgoing);
		const mergedIncoming = mergeRelationshipResultsByType(incoming);

		// Filter out zero-count and sort by count descending
		const discoveredOut = mergedOutgoing
			.filter((r) => r.count > 0)
			.sort((a, b) => b.count - a.count);
		const discoveredIn = mergedIncoming
			.filter((r) => r.count > 0)
			.sort((a, b) => b.count - a.count);

		const cacheNodeAri = rootNode?.id ?? nodeData.id;

		// Determine per-direction failures — only set the error flag if this
		// node is still the current exploration target.
		const outgoingFailed = discoveredOut.length === 0 && outgoingTraceIds.length > 0;
		const incomingFailed = discoveredIn.length === 0 && incomingTraceIds.length > 0;

		if (outgoingFailed || incomingFailed) {
			const currentNode = get(EffectiveExplorationNodeAtom);
			if (!currentNode || currentNode.id === nodeData.id) {
				set(DiscoveryFailuresAtom, {
					outgoing: outgoingFailed ? { traceIds: outgoingTraceIds } : null,
					incoming: incomingFailed ? { traceIds: incomingTraceIds } : null,
				});
			}
		}

		// Only cache results if no batch had failures — error'd results should
		// not be cached so that re-focusing the node re-sends all requests.
		const anyBatchFailed = outgoingTraceIds.length > 0 || incomingTraceIds.length > 0;

		if (!anyBatchFailed) {
			cacheDiscoveryResults(set, cacheNodeAri, 'outgoing', discoveredOut);
			cacheDiscoveryResults(set, cacheNodeAri, 'incoming', discoveredIn);
		} else {
			// Invalidate any previously cached entries for this node so a
			// subsequent focus re-sends all discovery requests.
			const outCacheKey = makeDiscoveryCacheKey(cacheNodeAri, 'outgoing');
			const inCacheKey = makeDiscoveryCacheKey(cacheNodeAri, 'incoming');
			set(QueryCacheAtom, (prev: Map<string, CachedQueryResult>) => {
				const next = new Map(prev);
				next.delete(outCacheKey);
				next.delete(inCacheKey);
				return next;
			});
		}

		return { rootNode, outgoing: discoveredOut, incoming: discoveredIn };
	} finally {
		// Always remove the in-flight entry so future callers don't get
		// stuck on a resolved/rejected promise.
		set(InflightDiscoveryAtom, (prev: Map<string, Promise<InflightDiscoveryResult>>) => {
			const next = new Map(prev);
			next.delete(nodeData.id);
			return next;
		});
	}
}

// ============================================================================
// Toggle a relationship (select/deselect + load nodes)
// ============================================================================

/**
 * Toggle a relationship in focus mode. If selected, deselect it.
 * If not selected, load its nodes (with caching) and select it.
 *
 * Usage:
 * ```
 * const toggle = useSetAtom(ToggleRelationshipAtom);
 * toggle({ sourceNode, relType, direction });
 * ```
 */
export const ToggleRelationshipAtom: WritableAtom<
	null,
	[
		payload: {
			sourceNode: ObjectNodeData;
			relType: string;
			direction: ExplorationDirection;
		},
	],
	Promise<void>
> & {
	init: null;
} = atom(
	null,
	async (
		get,
		set,
		payload: {
			sourceNode: ObjectNodeData;
			relType: string;
			direction: ExplorationDirection;
		},
	) => {
		const { sourceNode, relType, direction } = payload;
		const key = makeSelectedRelKey(sourceNode.id, direction, relType);

		// Check if already selected → toggle off
		const currentSelected = get(SelectedRelationshipsAtom);
		const isCurrentlySelected = currentSelected.some((r) => r.key === key);

		if (isCurrentlySelected) {
			set(SelectedRelationshipsAtom, (prev) => prev.filter((r) => r.key !== key));
			return;
		}

		// Check if already loading → ignore duplicate click
		const loadingKeys = get(LoadingRelKeysAtom);
		if (loadingKeys.has(key)) {
			return;
		}

		// Mark as loading
		set(LoadingRelKeysAtom, (prev: Set<string>) => {
			const next = new Set(prev);
			next.add(key);
			return next;
		});

		// Resolve display metadata from the display atoms so it persists
		// on the SelectedRelationship even when the active node shifts.
		const displayOutgoing = get(DisplayOutgoingAtom);
		const displayIncoming = get(DisplayIncomingAtom);
		const displayRel = [...displayOutgoing, ...displayIncoming].find((r) => r.type === relType);

		// Add to selected relationships immediately (shows loading state in graph)
		const selectedRel: SelectedRelationship = {
			key,
			sourceNodeAri: sourceNode.id,
			sourceNodeName: sourceNode.displayName || sourceNode.typename,
			relType,
			direction,
			displayType: displayRel?.displayType,
			displayVerb: displayRel?.displayVerb,
		};
		set(SelectedRelationshipsAtom, (prev) => [...prev, selectedRel]);

		try {
			// Check cache
			const cacheKey = makeNodesCacheKey(sourceNode.id, direction, relType);
			const cache = get(QueryCacheAtom);
			const cached = cache.get(cacheKey);

			let result: any;
			if (isCacheValid(cached)) {
				result = cached.result;
			} else if (isContentEntityLinksType(relType)) {
				// content-entity-links-entity (compound key like
				// "content_entity_links_entity::ConfluenceBlogpost") uses the
				// two-phase probe-then-hydrate approach.
				const contentResult = await set(DiscoverContentEntityLinksAtom, {
					nodeAri: sourceNode.id,
				});

				// Cache every per-type entry from both directions, so subsequent
				// toggles of sibling types hit cache instead of re-fetching.
				set(QueryCacheAtom, (prev) => {
					const next = new Map(prev);
					// Cache all per-type entries for both directions
					for (const dirKey of ['outgoing', 'incoming'] as ExplorationDirection[]) {
						const entries = dirKey === 'outgoing' ? contentResult.outgoing : contentResult.incoming;
						for (const entry of entries) {
							const ck = makeNodesCacheKey(sourceNode.id, dirKey, entry.type);
							next.set(ck, {
								result: buildSyntheticResult(entry.nodes || []),
								cachedAt: Date.now(),
							} as CachedQueryResult);
						}
					}
					return next;
				});

				// Now read back the cache for the requested key
				const updatedCache = get(QueryCacheAtom);
				const freshCached = updatedCache.get(cacheKey);
				result = freshCached?.result ?? buildSyntheticResult([]);
			} else {
				// Generate correct GraphQL query for target type. We resolve
				// the same RelationshipShape used at discovery time so the
				// load query matches the bridge topology (and the hydration
				// fragments cover the right typenames).
				const allRelationships = await get(twgRelationshipsAsyncAtom);
				const nodesMapRes = await get(twgNodesAsyncAtom);
				const nodesMap = nodesMapRes.success ? nodesMapRes.data : {};
				const atiNodeMap = get(AtiNodeMetadataMapAtom);

				let shape: RelationshipShape | null = null;
				if (allRelationships) {
					// Registry keys are hyphen-delimited and may differ from
					// `rel.name`, so locate the entry by matching on the
					// computed cypher name (same logic as
					// `getTargetNodeLabels`).
					for (const [id, rel] of Object.entries(allRelationships)) {
						const r = rel as Relationship;
						const cypherName = (r.name || id).replace(/-/g, '_');
						if (cypherName === relType) {
							shape = classifyRelationshipShape(r, relType, direction, atiNodeMap);
							break;
						}
					}
				}

				let graphQLOverride: string | null = null;
				if (allRelationships && nodesMap) {
					const targetLabels = getTargetNodeLabels(allRelationships, relType, direction);
					const expandedTargetLabels: string[] =
						shape && shape.kind !== 'direct'
							? collectHydrationTargetLabels([shape], allRelationships, atiNodeMap)
							: targetLabels;
					if (expandedTargetLabels.length > 0) {
						const graphQLTypes = convertLabelsToGraphQLTypes(expandedTargetLabels, nodesMap);
						if (graphQLTypes.length > 0) {
							const typesByName = get(GraphQLSchemaTypesByNameAtom);
							if (typesByName) {
								const queryResult = generateCypherQueryFromConcreteTypes(
									graphQLTypes,
									typesByName,
									{
										depth: 2,
										includeTypename: true,
										skipDeprecated: true,
										skipExperimental: true,
										autoSelectIdForObjects: true,
									},
									EXPLORATION_NODE_LIMIT,
								);
								if (queryResult) {
									graphQLOverride = queryResult.query;
								}
							}
						}
					}
				}

				const query = buildLoadNodesQuery(sourceNode.id, relType, direction, shape ?? undefined);
				// Use override atoms so the editor's CypherQueryAtom is never mutated.
				set(ExplorationCypherOverrideAtom, query);
				set(ExplorationGraphQLOverrideAtom, graphQLOverride ?? '');
				try {
					await set(ExecuteQueryAtom, 'none');
					result = get(NetworkResultAtom);
				} finally {
					set(ExplorationCypherOverrideAtom, null);
					set(ExplorationGraphQLOverrideAtom, null);
				}

				// Cache the result
				set(QueryCacheAtom, (prev) => {
					const next = new Map(prev);
					next.set(cacheKey, {
						result,
						cachedAt: Date.now(),
					} as CachedQueryResult);
					return next;
				});
			}
		} catch (error) {
			// On error, remove the relationship we optimistically added
			set(SelectedRelationshipsAtom, (prev) => prev.filter((r) => r.key !== key));
		} finally {
			// Always remove from loading set
			set(LoadingRelKeysAtom, (prev: Set<string>) => {
				const next = new Set(prev);
				next.delete(key);
				return next;
			});
		}
	},
);

// ============================================================================
// Remove a relationship
// ============================================================================

/**
 * Remove a specific relationship from focus mode selection.
 *
 * Usage: `const remove = useSetAtom(RemoveRelationshipAtom); remove(key);`
 */
export const RemoveRelationshipAtom: WritableAtom<null, [key: string], void> & {
	init: null;
} = atom(null, (_get, set, key: string) => {
	set(SelectedRelationshipsAtom, (prev) => prev.filter((r) => r.key !== key));
});

// ============================================================================
// Get cached relationship result (for graph building)
// ============================================================================

// ============================================================================
// Explore default query for the simple-home landing node
// ============================================================================
